import { orderBy } from 'lodash';
import mapValues from 'lodash/mapValues';
import partition from 'lodash/partition';
import React from 'react';
import { Redirect, Route } from 'react-router-dom';
import { CSSTransition } from 'react-transition-group';
import { useConfig } from '../../config/config.context';
import { bem } from '../../core/design/bem';
import UrlStateModalRouter from '../../modals/UrlStateModalRouter';
import { componentRouters, routeComponents } from '../Default/DefaultRoutes';
import { useIsFirstLoad, useLayout } from '../hooks/layout.hooks';
import layoutProps from '../layoutProps';
import './AnimatedLayout.scss';
import { withAnimatedRouterMatch } from './utils/routerUtils';

const DEFAULT_ANIMATION_DURATION = 2000;

const root = bem('AnimatedLayout');
const animatedRouteComponents = mapValues(routeComponents, withAnimatedRouterMatch);

const AnimatedLayout = () => {
  const layout = useLayout();
  const isFirstLoad = useIsFirstLoad();
  const { screens } = useConfig();
  // Order to register long paths (ex : /xxx/:slug) first
  const routerScreens = orderBy(
    Object.keys(screens).filter((screenId) => screens[screenId].path),
    (screenId) => screens[screenId].path.length,
    'desc',
  );

  const [redirectRoutes, otherRoutes] = partition(
    routerScreens,
    (screenId) => screens[screenId].forceRedirectTo,
  );

  return (
    <>
      <div className={root({ firstLoad: isFirstLoad }).mix(layout?.className)}>
        {redirectRoutes.map((screenId) => (
          <Redirect redirectTo to={screens[screenId].forceRedirectTo} />
        ))}
        {otherRoutes.map((screenId) => {
          const screen = screens[screenId];
          const Router = componentRouters[screen.type];
          if (Router) return <Router {...screen} pageId={screenId} />;

          const Component = animatedRouteComponents[screen.type];
          if (!Component) return null;

          return (
            <Route key={screenId} path={screen.path} exact>
              {({ match, ...props }) => (
                <CSSTransition
                  in={match !== null}
                  exit
                  timeout={{
                    appear: DEFAULT_ANIMATION_DURATION,
                    enter: DEFAULT_ANIMATION_DURATION,
                    exit: DEFAULT_ANIMATION_DURATION,
                  }}
                  classNames="AnimatedPage"
                  unmountOnExit
                  {...screen.layout?.cssTransition}
                >
                  <Component match={match} {...props} {...screen} pageId={screenId} />
                </CSSTransition>
              )}
            </Route>
          );
        })}
      </div>
      <UrlStateModalRouter />
    </>
  );
};

AnimatedLayout.propTypes = layoutProps;

export default AnimatedLayout;
